import { makeInitialsAvatar } from "./avatars.ts";
import { type DemoCompanyId } from "./identifiers.ts";

export type DemoContactCategory =
  | "SaaS"
  | "Office"
  | "Logistics"
  | "Professional"
  | "Marketing"
  | "Client"
  | "Finance";

export type DemoContact = {
  id: string;
  name: string;
  registrationNumber: string;
  vatNumber?: string;
  iban: string;
  bankName: string;
  bankBic: string;
  email?: string;
  phone?: string;
  city: string;
  street: string;
  zip: string;
  country: "AT" | "CZ" | "DE" | "PL" | "FR" | "NL" | "GB" | "ES" | "IE" | "LU";
  // Which group company has the most activity with this counterparty.
  companyId: DemoCompanyId;
  lastTransactionAt: string;
  totalSpent: number;
  totalReceived: number;
  currency: "EUR" | "CZK" | "PLN" | "USD" | "GBP";
  category: DemoContactCategory;
  tags: string[];
  imgSrc: string;
};

const make = (c: Omit<DemoContact, "imgSrc">): DemoContact => ({
  ...c,
  imgSrc: makeInitialsAvatar(c.name),
});

import {
  DEMO_COMPANY_HOLDINGS_AG,
  DEMO_COMPANY_LABS_SP,
  DEMO_COMPANY_STUDIO_SRO,
  DEMO_COMPANY_TRADE_LTD,
  DEMO_COMPANY_VENTURES_GMBH,
} from "./identifiers.ts";

export const DEMO_CONTACTS: DemoContact[] = [
  // --- SaaS suppliers (cross-company) ---
  make({
    id: "demo-contact-vexa",
    name: "Vexa Cloud B.V.",
    registrationNumber: "82914731",
    vatNumber: "NL864218429B01",
    iban: "NL30 INGB 0009 4218 42",
    bankName: "ING Bank N.V.",
    bankBic: "INGBNL2A",
    email: "billing@vexa-cloud.demo",
    city: "Amsterdam",
    street: "Prinsengracht 482",
    zip: "1017 KG",
    country: "NL",
    companyId: DEMO_COMPANY_HOLDINGS_AG,
    lastTransactionAt: "2026-05-19T08:14:00.000Z",
    totalSpent: 18_240,
    totalReceived: 0,
    currency: "EUR",
    category: "SaaS",
    tags: ["Cloud", "Infra"],
  }),
  make({
    id: "demo-contact-beampoint",
    name: "Beampoint Analytics Ltd.",
    registrationNumber: "12348792",
    vatNumber: "GB348721094",
    iban: "GB29 NWBK 6016 1331 9268 19",
    bankName: "NatWest",
    bankBic: "NWBKGB2L",
    email: "ar@beampoint.demo",
    city: "London",
    street: "84 Brick Lane",
    zip: "E1 6RL",
    country: "GB",
    companyId: DEMO_COMPANY_HOLDINGS_AG,
    lastTransactionAt: "2026-05-18T11:22:00.000Z",
    totalSpent: 5_880,
    totalReceived: 0,
    currency: "GBP",
    category: "SaaS",
    tags: ["Analytics"],
  }),
  make({
    id: "demo-contact-pixelpath",
    name: "Pixelpath Studio GmbH",
    registrationNumber: "HRB 198472",
    vatNumber: "DE284910283",
    iban: "DE89 3704 0044 0532 0130 00",
    bankName: "Commerzbank",
    bankBic: "COBADEFFXXX",
    email: "billing@pixelpath.demo",
    city: "Hamburg",
    street: "Reeperbahn 12",
    zip: "20359",
    country: "DE",
    companyId: DEMO_COMPANY_VENTURES_GMBH,
    lastTransactionAt: "2026-05-14T18:00:00.000Z",
    totalSpent: 4_590,
    totalReceived: 0,
    currency: "EUR",
    category: "SaaS",
    tags: ["Design tool"],
  }),
  make({
    id: "demo-contact-quillstream",
    name: "Quillstream Ltd.",
    registrationNumber: "13284910",
    vatNumber: "GB291847261",
    iban: "GB95 BARC 2010 1538 9472 81",
    bankName: "Barclays",
    bankBic: "BARCGB22",
    email: "billing@quillstream.demo",
    city: "Manchester",
    street: "21 Spring Gardens",
    zip: "M2 1FB",
    country: "GB",
    companyId: DEMO_COMPANY_TRADE_LTD,
    lastTransactionAt: "2026-05-17T10:00:00.000Z",
    totalSpent: 1_240,
    totalReceived: 0,
    currency: "GBP",
    category: "SaaS",
    tags: ["Editorial"],
  }),
  make({
    id: "demo-contact-halofield",
    name: "Halofield Systems AG",
    registrationNumber: "FN 384721 v",
    vatNumber: "ATU49281047",
    iban: "AT94 1900 0100 0428 9105",
    bankName: "Raiffeisen Bank International AG",
    bankBic: "RZBAATWW",
    email: "ar@halofield.demo",
    city: "Vienna",
    street: "Stephansplatz 8",
    zip: "1010",
    country: "AT",
    companyId: DEMO_COMPANY_HOLDINGS_AG,
    lastTransactionAt: "2026-05-12T13:42:00.000Z",
    totalSpent: 9_840,
    totalReceived: 0,
    currency: "EUR",
    category: "SaaS",
    tags: ["Identity"],
  }),
  make({
    id: "demo-contact-tetra-drive",
    name: "Tetra Drive Sp. z o.o.",
    registrationNumber: "KRS 0000392184",
    vatNumber: "PL5252394827",
    iban: "PL61 1090 1014 0000 0712 1981 2470",
    bankName: "Santander Bank Polska",
    bankBic: "WBKPPLPP",
    email: "billing@tetradrive.demo",
    city: "Kraków",
    street: "Rynek Główny 12",
    zip: "31-042",
    country: "PL",
    companyId: DEMO_COMPANY_LABS_SP,
    lastTransactionAt: "2026-05-16T09:00:00.000Z",
    totalSpent: 12_840,
    totalReceived: 0,
    currency: "PLN",
    category: "SaaS",
    tags: ["DevOps"],
  }),

  // --- Office / Operations ---
  make({
    id: "demo-contact-brickline",
    name: "Brickline Real Estate GmbH",
    registrationNumber: "HRB 384921",
    vatNumber: "DE291847261",
    iban: "DE17 2007 0024 0192 0001 11",
    bankName: "Deutsche Bank",
    bankBic: "DEUTDEHH",
    email: "lease@brickline.demo",
    city: "Berlin",
    street: "Friedrichstraße 90",
    zip: "10117",
    country: "DE",
    companyId: DEMO_COMPANY_VENTURES_GMBH,
    lastTransactionAt: "2026-05-01T08:00:00.000Z",
    totalSpent: 124_800,
    totalReceived: 0,
    currency: "EUR",
    category: "Office",
    tags: ["Rent"],
  }),
  make({
    id: "demo-contact-aurea",
    name: "Aurea Power s.r.o.",
    registrationNumber: "01928374",
    vatNumber: "CZ01928374",
    iban: "CZ81 0300 0000 0014 4400 8881",
    bankName: "ČSOB, a.s.",
    bankBic: "CEKOCZPP",
    email: "fakturace@aurea-power.demo",
    city: "Brno",
    street: "Mendlovo náměstí 1a",
    zip: "603 00",
    country: "CZ",
    companyId: DEMO_COMPANY_STUDIO_SRO,
    lastTransactionAt: "2026-05-02T05:30:00.000Z",
    totalSpent: 41_280,
    totalReceived: 0,
    currency: "CZK",
    category: "Office",
    tags: ["Energy"],
  }),
  make({
    id: "demo-contact-mintway",
    name: "Mintway Telecom AG",
    registrationNumber: "FN 218947 g",
    vatNumber: "ATU18472918",
    iban: "AT38 1200 0100 5028 4731",
    bankName: "BAWAG P.S.K.",
    bankBic: "BAWAATWW",
    email: "support@mintway.demo",
    city: "Vienna",
    street: "Schwedenplatz 4",
    zip: "1010",
    country: "AT",
    companyId: DEMO_COMPANY_HOLDINGS_AG,
    lastTransactionAt: "2026-05-10T11:11:00.000Z",
    totalSpent: 4_980,
    totalReceived: 0,
    currency: "EUR",
    category: "Office",
    tags: ["Telecom"],
  }),
  make({
    id: "demo-contact-solune",
    name: "Solune Office Supplies Sp. z o.o.",
    registrationNumber: "KRS 0000482910",
    vatNumber: "PL5283910482",
    iban: "PL21 1140 2004 0000 3902 9472 1018",
    bankName: "mBank S.A.",
    bankBic: "BREXPLPW",
    email: "shop@solune.demo",
    city: "Warsaw",
    street: "ul. Marszałkowska 142",
    zip: "00-061",
    country: "PL",
    companyId: DEMO_COMPANY_LABS_SP,
    lastTransactionAt: "2026-05-15T09:18:00.000Z",
    totalSpent: 6_410,
    totalReceived: 0,
    currency: "PLN",
    category: "Office",
    tags: ["Supplies"],
  }),

  // --- Logistics / Travel ---
  make({
    id: "demo-contact-aerial-cargo",
    name: "Aerial Cargo B.V.",
    registrationNumber: "60294817",
    vatNumber: "NL618472918B01",
    iban: "NL52 RABO 0192 8473 91",
    bankName: "Rabobank",
    bankBic: "RABONL2U",
    email: "ar@aerial-cargo.demo",
    city: "Rotterdam",
    street: "Wilhelminakade 22",
    zip: "3072 AP",
    country: "NL",
    companyId: DEMO_COMPANY_HOLDINGS_AG,
    lastTransactionAt: "2026-05-12T17:25:00.000Z",
    totalSpent: 7_840,
    totalReceived: 0,
    currency: "EUR",
    category: "Logistics",
    tags: ["Shipping"],
  }),
  make({
    id: "demo-contact-coastward",
    name: "Coastward Logistics s.r.o.",
    registrationNumber: "04827193",
    vatNumber: "CZ04827193",
    iban: "CZ55 0300 0000 0017 7045 0086",
    bankName: "ČSOB, a.s.",
    bankBic: "CEKOCZPP",
    email: "info@coastward.demo",
    city: "Plzeň",
    street: "Husova 4",
    zip: "301 00",
    country: "CZ",
    companyId: DEMO_COMPANY_STUDIO_SRO,
    lastTransactionAt: "2026-05-08T15:00:00.000Z",
    totalSpent: 12_840,
    totalReceived: 0,
    currency: "CZK",
    category: "Logistics",
    tags: ["Domestic"],
  }),
  make({
    id: "demo-contact-aerlite-travel",
    name: "Aerlite Travel GmbH",
    registrationNumber: "HRB 718294",
    vatNumber: "DE581904721",
    iban: "DE56 1001 0010 0030 1184 00",
    bankName: "Deutsche Bank",
    bankBic: "DEUTDEHH",
    email: "bookings@aerlite-travel.demo",
    city: "Munich",
    street: "Marienplatz 8",
    zip: "80331",
    country: "DE",
    companyId: DEMO_COMPANY_VENTURES_GMBH,
    lastTransactionAt: "2026-05-04T08:18:00.000Z",
    totalSpent: 9_240,
    totalReceived: 0,
    currency: "EUR",
    category: "Logistics",
    tags: ["Business travel"],
  }),

  // --- Professional services ---
  make({
    id: "demo-contact-northpoint-advisors",
    name: "Northpoint Advisors LLP",
    registrationNumber: "OC382910",
    vatNumber: "GB182734918",
    iban: "GB18 RBOS 1610 0500 0918 24",
    bankName: "RBS",
    bankBic: "RBOSGB2L",
    email: "billing@northpoint-advisors.demo",
    city: "London",
    street: "1 Pall Mall East",
    zip: "SW1Y 5AU",
    country: "GB",
    companyId: DEMO_COMPANY_TRADE_LTD,
    lastTransactionAt: "2026-05-09T11:00:00.000Z",
    totalSpent: 14_500,
    totalReceived: 0,
    currency: "GBP",
    category: "Professional",
    tags: ["Legal"],
  }),
  make({
    id: "demo-contact-vanguard-accounting",
    name: "Vanguard Accounting GmbH",
    registrationNumber: "FN 184729 k",
    vatNumber: "ATU38472918",
    iban: "AT64 2011 1284 7392 8472",
    bankName: "Erste Bank",
    bankBic: "GIBAATWW",
    email: "ar@vanguard-accounting.demo",
    city: "Linz",
    street: "Hauptplatz 12",
    zip: "4020",
    country: "AT",
    companyId: DEMO_COMPANY_HOLDINGS_AG,
    lastTransactionAt: "2026-05-05T09:00:00.000Z",
    totalSpent: 6_840,
    totalReceived: 0,
    currency: "EUR",
    category: "Professional",
    tags: ["Accounting"],
  }),
  make({
    id: "demo-contact-plumb-audit",
    name: "Plumb Audit & Co.",
    registrationNumber: "12091847",
    vatNumber: "GB920184729",
    iban: "GB12 HSBC 4019 2018 4729 18",
    bankName: "HSBC",
    bankBic: "HBUKGB4B",
    email: "billing@plumb-audit.demo",
    city: "London",
    street: "8 Canary Wharf",
    zip: "E14 5AB",
    country: "GB",
    companyId: DEMO_COMPANY_HOLDINGS_AG,
    lastTransactionAt: "2026-03-21T11:30:00.000Z",
    totalSpent: 22_400,
    totalReceived: 0,
    currency: "GBP",
    category: "Professional",
    tags: ["Audit", "Annual"],
  }),
  make({
    id: "demo-contact-brightcircle-insurance",
    name: "BrightCircle Insurance AG",
    registrationNumber: "FN 491820 a",
    vatNumber: "ATU58372910",
    iban: "AT23 3000 0000 0058 3729",
    bankName: "Raiffeisen Bank International AG",
    bankBic: "RZBAATWW",
    email: "policies@brightcircle.demo",
    city: "Salzburg",
    street: "Mozartplatz 4",
    zip: "5020",
    country: "AT",
    companyId: DEMO_COMPANY_HOLDINGS_AG,
    lastTransactionAt: "2026-05-02T10:00:00.000Z",
    totalSpent: 8_240,
    totalReceived: 0,
    currency: "EUR",
    category: "Professional",
    tags: ["Insurance"],
  }),

  // --- Marketing / Media ---
  make({
    id: "demo-contact-sparrow-media",
    name: "Sparrow Media Group SA",
    registrationNumber: "B 192847",
    vatNumber: "LU29184729",
    iban: "LU17 0080 0001 0050 6810",
    bankName: "BCEE",
    bankBic: "BCEELULL",
    email: "ar@sparrow-media.demo",
    city: "Luxembourg",
    street: "38 avenue John F. Kennedy",
    zip: "L-1855",
    country: "LU",
    companyId: DEMO_COMPANY_HOLDINGS_AG,
    lastTransactionAt: "2026-05-03T10:00:00.000Z",
    totalSpent: 18_200,
    totalReceived: 0,
    currency: "EUR",
    category: "Marketing",
    tags: ["Campaigns"],
  }),
  make({
    id: "demo-contact-tilly-creative",
    name: "Tilly Creative s.r.o.",
    registrationNumber: "08294710",
    vatNumber: "CZ08294710",
    iban: "CZ70 2700 0000 0030 1184 0023",
    bankName: "UniCredit Bank",
    bankBic: "BACXCZPP",
    email: "studio@tillycreative.demo",
    city: "Prague",
    street: "Truhlářská 10",
    zip: "110 00",
    country: "CZ",
    companyId: DEMO_COMPANY_STUDIO_SRO,
    lastTransactionAt: "2026-04-29T13:00:00.000Z",
    totalSpent: 142_300,
    totalReceived: 0,
    currency: "CZK",
    category: "Marketing",
    tags: ["Branding"],
  }),
  make({
    id: "demo-contact-halo-studios",
    name: "Halo Studios B.V.",
    registrationNumber: "61029384",
    vatNumber: "NL918273645B01",
    iban: "NL18 ABNA 0192 8473 91",
    bankName: "ABN AMRO",
    bankBic: "ABNANL2A",
    email: "studio@halo-studios.demo",
    city: "Utrecht",
    street: "Oudegracht 220",
    zip: "3511 NS",
    country: "NL",
    companyId: DEMO_COMPANY_VENTURES_GMBH,
    lastTransactionAt: "2026-05-07T14:00:00.000Z",
    totalSpent: 12_400,
    totalReceived: 0,
    currency: "EUR",
    category: "Marketing",
    tags: ["Video", "Brand"],
  }),

  // --- Clients (incoming revenue) ---
  make({
    id: "demo-contact-northpoint-capital",
    name: "Northpoint Capital AG",
    registrationNumber: "FN 318472 m",
    vatNumber: "ATU49371928",
    iban: "AT44 2011 1234 9182 7361",
    bankName: "Erste Bank",
    bankBic: "GIBAATWW",
    email: "treasury@northpoint-capital.demo",
    city: "Vienna",
    street: "Schwarzenbergplatz 8",
    zip: "1030",
    country: "AT",
    companyId: DEMO_COMPANY_HOLDINGS_AG,
    lastTransactionAt: "2026-05-19T09:30:00.000Z",
    totalSpent: 0,
    totalReceived: 384_000,
    currency: "EUR",
    category: "Client",
    tags: ["Strategic"],
  }),
  make({
    id: "demo-contact-marisco-industries",
    name: "Marisco Industries S.A.",
    registrationNumber: "B 482910",
    vatNumber: "ES B48291047",
    iban: "ES17 0049 0001 5021 8472 3918",
    bankName: "Banco Santander",
    bankBic: "BSCHESMM",
    email: "ap@marisco.demo",
    city: "Barcelona",
    street: "Passeig de Gràcia 92",
    zip: "08008",
    country: "ES",
    companyId: DEMO_COMPANY_HOLDINGS_AG,
    lastTransactionAt: "2026-05-10T11:00:00.000Z",
    totalSpent: 0,
    totalReceived: 218_500,
    currency: "EUR",
    category: "Client",
    tags: ["Manufacturing"],
  }),
  make({
    id: "demo-contact-pelegrini-hospitality",
    name: "Pelegrini Hospitality Group s.r.o.",
    registrationNumber: "07291847",
    vatNumber: "CZ07291847",
    iban: "CZ47 0600 0000 0008 9012 3456",
    bankName: "MONETA Money Bank",
    bankBic: "AGBACZPP",
    email: "ap@pelegrini.demo",
    city: "Prague",
    street: "Vinohradská 174",
    zip: "130 00",
    country: "CZ",
    companyId: DEMO_COMPANY_STUDIO_SRO,
    lastTransactionAt: "2026-05-13T16:00:00.000Z",
    totalSpent: 0,
    totalReceived: 437_500,
    currency: "CZK",
    category: "Client",
    tags: ["Hospitality"],
  }),
  make({
    id: "demo-contact-atria-health",
    name: "Atria Health s.r.o.",
    registrationNumber: "08361729",
    vatNumber: "CZ08361729",
    iban: "CZ34 0100 0000 0023 4023 0011",
    bankName: "Komerční banka, a.s.",
    bankBic: "KOMBCZPP",
    email: "ap@atriahealth.demo",
    city: "Brno",
    street: "Pekařská 64",
    zip: "602 00",
    country: "CZ",
    companyId: DEMO_COMPANY_STUDIO_SRO,
    lastTransactionAt: "2026-05-17T14:32:00.000Z",
    totalSpent: 0,
    totalReceived: 218_750,
    currency: "CZK",
    category: "Client",
    tags: ["Healthcare"],
  }),
  make({
    id: "demo-contact-brightsky-media",
    name: "BrightSky Media SA",
    registrationNumber: "RCS B 192847",
    vatNumber: "FR48291047283",
    iban: "FR14 2004 1010 0505 0001 3M02 606",
    bankName: "BNP Paribas",
    bankBic: "BNPAFRPP",
    email: "ap@brightsky-media.demo",
    city: "Paris",
    street: "16 rue de Berri",
    zip: "75008",
    country: "FR",
    companyId: DEMO_COMPANY_VENTURES_GMBH,
    lastTransactionAt: "2026-05-15T10:00:00.000Z",
    totalSpent: 0,
    totalReceived: 178_900,
    currency: "EUR",
    category: "Client",
    tags: ["Media"],
  }),
  make({
    id: "demo-contact-solstice-bank-partner",
    name: "Solstice Bank Partner s.r.o.",
    registrationNumber: "09182734",
    vatNumber: "CZ09182734",
    iban: "CZ12 0100 0000 0011 9203 8455",
    bankName: "Komerční banka, a.s.",
    bankBic: "KOMBCZPP",
    email: "treasury@solstice-bank.demo",
    city: "Prague",
    street: "Na Příkopě 14",
    zip: "110 00",
    country: "CZ",
    companyId: DEMO_COMPANY_STUDIO_SRO,
    lastTransactionAt: "2026-04-22T10:11:00.000Z",
    totalSpent: 0,
    totalReceived: 521_000,
    currency: "CZK",
    category: "Client",
    tags: ["Strategic", "Fintech"],
  }),
  make({
    id: "demo-contact-kepler-robotics",
    name: "Kepler Robotics Sp. z o.o.",
    registrationNumber: "KRS 0000291847",
    vatNumber: "PL5292847291",
    iban: "PL81 1090 2402 0000 0001 4729 1847",
    bankName: "Santander Bank Polska",
    bankBic: "WBKPPLPP",
    email: "ap@kepler-robotics.demo",
    city: "Wrocław",
    street: "Rynek 8",
    zip: "50-101",
    country: "PL",
    companyId: DEMO_COMPANY_LABS_SP,
    lastTransactionAt: "2026-05-08T11:00:00.000Z",
    totalSpent: 0,
    totalReceived: 312_400,
    currency: "PLN",
    category: "Client",
    tags: ["R&D"],
  }),
  make({
    id: "demo-contact-mirage-apparel",
    name: "Mirage Apparel Ltd.",
    registrationNumber: "12918472",
    vatNumber: "GB291847291",
    iban: "GB47 LOYD 3091 0182 9472 81",
    bankName: "Lloyds",
    bankBic: "LOYDGB21",
    email: "ap@mirage-apparel.demo",
    city: "Manchester",
    street: "Deansgate 184",
    zip: "M3 4LY",
    country: "GB",
    companyId: DEMO_COMPANY_TRADE_LTD,
    lastTransactionAt: "2026-05-16T11:00:00.000Z",
    totalSpent: 0,
    totalReceived: 94_200,
    currency: "GBP",
    category: "Client",
    tags: ["Retail"],
  }),

  // --- Payment gateways + bank fees ---
  make({
    id: "demo-contact-payflow-gateway",
    name: "Payflow Gateway Ltd.",
    registrationNumber: "10298471",
    vatNumber: "IE3294810LH",
    iban: "IE29 AIBK 9311 5212 3456 78",
    bankName: "AIB",
    bankBic: "AIBKIE2D",
    email: "settlements@payflow-gateway.demo",
    city: "Dublin",
    street: "1 Grand Canal Square",
    zip: "D02 H210",
    country: "IE",
    companyId: DEMO_COMPANY_VENTURES_GMBH,
    lastTransactionAt: "2026-05-20T16:00:00.000Z",
    totalSpent: 0,
    totalReceived: 184_550,
    currency: "EUR",
    category: "Finance",
    tags: ["Payment gateway"],
  }),
  make({
    id: "demo-contact-fxbridge-treasury",
    name: "FXBridge Treasury Services",
    registrationNumber: "L20928",
    vatNumber: "LU18472918",
    iban: "LU37 0080 0001 0050 6810",
    bankName: "BCEE",
    bankBic: "BCEELULL",
    email: "ops@fxbridge.demo",
    city: "Luxembourg",
    street: "10 boulevard Royal",
    zip: "L-2449",
    country: "LU",
    companyId: DEMO_COMPANY_HOLDINGS_AG,
    lastTransactionAt: "2026-05-18T12:00:00.000Z",
    totalSpent: 1_240,
    totalReceived: 0,
    currency: "EUR",
    category: "Finance",
    tags: ["FX"],
  }),
];
